import { useControllableState } from '@radix-ui/react-use-controllable-state'
import clsx from 'clsx'
import { forwardRef, useMemo, useRef } from 'react'

import { useScheduledLayoutEffect } from 'slab/hooks/useScheduledLayoutEffect'

interface DateSegmentProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  onChange: (value: number | null) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  placeholder?: string;
  segment: 'day' | 'month' | 'year';
  size?: number;
  value: number | null;
}

const SEGMENT_CONFIG = {
  day: {
    ariaLabel: 'Day',
    getDefault: () => new Date().getDate(),
    max: 31,
    min: 1,
    size: 2,
  },
  month: {
    ariaLabel: 'Month',
    getDefault: () => new Date().getMonth() + 1,
    max: 12,
    min: 1,
    size: 2,
  },
  year: {
    ariaLabel: 'Year',
    getDefault: () => new Date().getFullYear(),
    max: 9999,
    min: 1,
    size: 4,
  },
}

function padDateSegment (value: number, length: number) {
  return String(value).padStart(length, '0')
}

export const DateSegment = forwardRef<HTMLInputElement, DateSegmentProps>(
  (
    { onChange, onNext, onPrevious, placeholder, segment, value: valueProp, ...props },
    forwardedRef
  ) => {
    const [value, setValue] = useControllableState({
      defaultProp: null,
      prop: valueProp,
      onChange,
    })

    const lastKeyPressRef = useRef<string | null>(null)

    const scheduleLayoutEffect = useScheduledLayoutEffect()

    const { ariaLabel, getDefault, max, min, size } = useMemo(
      () => SEGMENT_CONFIG[segment],
      [segment]
    )

    const paddedValue = useMemo(() => {
      if (value === null) return ''
      return padDateSegment(value, size ?? 0)
    }, [value, size])

    function shouldAdvance (value: string) {
      if (!value) return false
      if (value.length >= size) return true

      switch (segment) {
        case 'day':
          return Number(value) > 3
        case 'month':
          return Number(value) > 1
      }

      return false
    }

    return (
      <div
        data-autosize={paddedValue || placeholder}
        className='inline-block relative after:content-[attr(data-autosize)] after:invisible after:whitespace-pre-wrap after:body after:w-auto after:min-w-4 after:text-center'
      >
        <input
          {...props}
          aria-label={ariaLabel}
          className={clsx(
            'appearance-none bg-none bg-transparent border-none focus:ring-transparent caret-transparent rounded-sm body p-0 placeholder:text-gray-500 text-gray-800 aria-invalid:text-red-700 outline outline-2 !outline-offset-0 outline-transparent focus:outline-thatch-500 text-center absolute inset-0'
          )}
          data-date-segment={segment}
          inputMode='numeric'
          ref={forwardedRef}
          onBlur={(event) => {
            if (value !== null && (value > max || value < min)) {
              setValue(Math.max(min, Math.min(max, value)))
            }

            props.onBlur?.(event)
          }}
          onChange={(event) => {
            const { value } = event.target

            const trimmedValue = value.replace(/\D/g, '').slice(-1 * size)
            const parsedValue =
              trimmedValue !== '' ? Number(trimmedValue) : null

            if (lastKeyPressRef.current === 'Backspace' && parsedValue === 0) {
              setValue(null)
            } else {
              setValue(parsedValue)
            }

            if (shouldAdvance(trimmedValue)) {
              scheduleLayoutEffect('advance', () => {
                onNext?.()
              })
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp') {
              event.preventDefault()

              if (value === null) {
                setValue(getDefault())
              } else {
                setValue(Math.min(max, value + 1))
              }
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault()

              if (value === null) {
                setValue(getDefault())
              } else {
                setValue(Math.max(min, value - 1))
              }
            }

            if (event.key === 'ArrowRight') {
              if (onNext) {
                event.preventDefault()
                onNext()
              }
            }

            if (event.key === 'ArrowLeft') {
              if (onPrevious) {
                event.preventDefault()
                onPrevious()
              }
            }

            if (event.key === 'Backspace' && value === null) {
              if (onPrevious) {
                event.preventDefault()
                onPrevious()
              }
            }

            props.onKeyDown?.(event)

            lastKeyPressRef.current = event.key
          }}
          onFocus={(event) => {
            event.target.select()

            props.onFocus?.(event)
          }}
          placeholder={placeholder}
          size={1}
          type='text'
          value={paddedValue}
        />
      </div>
    )
  }
)
